import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { DataTableWrapper } from "@trillion/ui/components/admin/data-table-wrapper";
import { ConfirmDialog } from "@trillion/ui/components/admin/confirm-dialog";
import { useDataTableState } from "@trillion/ui";
import { adminApi } from "@/lib/admin-api";
import { describeApiError } from "@/lib/api-error";
import { useNetworkOptions } from "@/hooks/use-network-options";
import { getBrandColumns } from "@/components/brands/brand-columns";
import type { ProductBrand } from "@/types";

const PAGE_SIZE = 20;
const ALL_NETWORKS = "all";

export function BrandsPage() {
  const navigate = useNavigate();
  const { networks } = useNetworkOptions();
  const [networkFilter, setNetworkFilter] = useState<string>(ALL_NETWORKS);

  const sourceId = networkFilter === ALL_NETWORKS ? undefined : Number(networkFilter);

  const t = useDataTableState<ProductBrand>({
    fetcher: async (p) => {
      const filterSourceId = p.filters?.sourceId as number | undefined;
      const result = await adminApi.brands.list({
        page: p.page,
        pageSize: p.pageSize,
        search: p.search || undefined,
        sourceId: filterSourceId,
        sortBy: p.sortBy,
        sortDesc: p.sortDesc,
      });
      return { items: result.items, totalCount: result.total_count };
    },
    onDelete: async (brand) => {
      // A 409 here means products still reference the brand — surfaced verbatim via describeApiError.
      await adminApi.brands.delete(brand.brand_id);
      toast.success("Brand deleted");
    },
    filters: { sourceId },
    pageSize: PAGE_SIZE,
    onError: (err) =>
      toast.error("Request failed", {
        description: describeApiError(err),
      }),
  });

  const networkNames = useMemo(
    () => new Map(networks.map((n) => [n.source_id, n.name])),
    [networks]
  );

  const { setDeleteTarget } = t;
  const columns = useMemo(
    () =>
      getBrandColumns({
        onEdit: (brand) => navigate(`/brands/${brand.brand_id}`),
        onDelete: (brand) => setDeleteTarget(brand),
        networkNames,
      }),
    [navigate, setDeleteTarget, networkNames]
  );

  const table = useReactTable({
    data: t.data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Brands"
        description="Manage brands (advertisers/merchants) per network"
        actions={
          <Button onClick={() => navigate("/brands/new")}>
            <Plus className="mr-2 size-4" />
            New Brand
          </Button>
        }
      />

      <DataTableWrapper
        title="All Brands"
        description={`${t.totalCount} brand${t.totalCount !== 1 ? "s" : ""} total`}
        isLoading={t.isLoading}
        toolbar={
          <div className="flex items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search brands..."
                value={t.search}
                onChange={(e) => t.setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={networkFilter} onValueChange={setNetworkFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All networks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_NETWORKS}>All networks</SelectItem>
                {networks.map((n) => (
                  <SelectItem key={n.source_id} value={String(n.source_id)}>
                    {n.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
        pagination={
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {t.page} of {t.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => t.setPage(Math.max(1, t.page - 1))}
                disabled={t.page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => t.setPage(t.page + 1)}
                disabled={t.page >= t.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        }
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No brands found.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </DataTableWrapper>

      <ConfirmDialog
        open={t.deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) t.setDeleteTarget(null);
        }}
        title="Delete Brand"
        description={`Are you sure you want to delete "${t.deleteTarget?.name}"? Products under this brand must be deleted first.`}
        confirmLabel="Delete"
        onConfirm={t.confirmDelete}
        variant="destructive"
        isLoading={t.isDeleting}
      />
    </div>
  );
}
